import { Box, Card, CardActionArea, Stack, Typography } from '@mui/material';
import { Add, ChevronRight } from '@mui/icons-material';

import { Outlet, useNavigate } from 'react-router';
import { VehicleTypeIcon } from 'dg-web-shared/common/components/material-ui/VehicleTypeIcon';
import { Localized } from 'dg-web-shared/lib/Localization';
import { ParkingaboLayoutWithHeader } from '../../../components/layout/ParkingaboLayoutWithHeader';
import { VehicleQrCodeDrawer } from '../../../components/VehicleQrCodeDrawer';
import {
    ParkingaboVehicle,
    VehicleLicensePlateType,
} from 'dg-web-shared/common/models/Vehicle';
import { formatBadgeLabelByType } from 'product-shared/tenant/TenantVehicleIdentificationUtils';
import { useCustomerContext } from '../../../components/CustomerProvider.tsx';
import { VehicleIdentification } from 'product-shared/parkingabo/ParkingaboOperatingMode.tsx';

export function VehiclesRoute() {
    const { user, vehicles } = useCustomerContext();
    return (
        <ParkingaboLayoutWithHeader
            title={
                <Localized
                    de="Fahrzeuge"
                    fr="Véhicules"
                    it="Veicoli"
                    en="Vehicles"
                />
            }
            backTo={'..'}
            scrollable
        >
            <Stack
                direction="column"
                spacing={2}
                sx={{
                    justifyContent: 'flex-start',
                    alignItems: 'stretch',
                }}
            >
                {vehicles.data.map(vehicle => (
                    <VehicleCard
                        key={vehicle.licensePlateNr}
                        vehicleIdentification={user.role.vehicleIdentification}
                        vehicle={vehicle}
                    />
                ))}
                <AddVehicleCard />
            </Stack>
            <Outlet />
            {user.role.vehicleIdentification.includes(
                VehicleIdentification.QR,
            ) && <VehicleQrCodeDrawer vehicles={vehicles.data} />}
        </ParkingaboLayoutWithHeader>
    );
}

function VehicleCard({
    vehicle,
    vehicleIdentification,
}: {
    vehicle: ParkingaboVehicle;
    vehicleIdentification: VehicleIdentification[];
}) {
    const navigate = useNavigate();
    const lpMissing =
        vehicleIdentification.includes(VehicleIdentification.LICENSE_PLATE) &&
        !vehicle.licensePlateNr;
    return (
        <BaseCard
            onClick={() => {
                navigate(vehicle.customerTenantCarId);
            }}
        >
            <VehicleTypeIcon
                type={vehicle.type || VehicleLicensePlateType.CAR}
                sx={theme => {
                    const h3FontSize = theme.typography.h3.fontSize;
                    return {
                        fontSize:
                            typeof h3FontSize === 'number'
                                ? h3FontSize * 2
                                : theme.typography.h2.fontSize,
                    };
                }}
            />
            <Box sx={{ flexGrow: 1 }}>
                <Typography
                    sx={{
                        fontWeight: 'bold',
                    }}
                >
                    {vehicle.description}
                </Typography>
                {lpMissing ? (
                    <Typography
                        sx={{
                            color: theme => theme.palette.error.main,
                            fontStyle: 'italic',
                        }}
                    >
                        <Localized
                            de="KENNZEICHEN FEHLT"
                            fr="PLAQUE MANQUANTE"
                            it="TARGA MANCANTE"
                            en="MISSING LP"
                        />
                    </Typography>
                ) : (
                    <Typography>
                        {vehicle.licensePlateNr
                            ? vehicle.licensePlateNr
                            : formatBadgeLabelByType(
                                  vehicle.badgeLabelNr,
                                  vehicle.badgeType,
                              )}
                        {vehicle.country && ` (${vehicle.country})`}
                    </Typography>
                )}
            </Box>
            <ChevronRight />
        </BaseCard>
    );
}

function AddVehicleCard() {
    const navigate = useNavigate();
    return (
        <BaseCard onClick={() => navigate('add')}>
            <Typography
                component="div"
                sx={{
                    flexGrow: 1,
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                }}
            >
                <Localized
                    de="Fahrzeug hinzufügen"
                    fr="Ajouter un véhicule"
                    it="Aggiungi veicolo"
                    en="Add vehicle"
                />
            </Typography>
            <Add />
        </BaseCard>
    );
}

function BaseCard({
    onClick,
    children,
}: {
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <Card>
            <CardActionArea
                onClick={onClick}
                sx={theme => ({
                    padding: theme.spacing(2),
                    paddingRight: theme.spacing(3),
                    minHeight: theme.spacing(10),
                })}
            >
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {children}
                </Stack>
            </CardActionArea>
        </Card>
    );
}
